import { action, makeObservable, runInAction } from 'mobx';

import { SortDirection } from 'constants/SortDirection';
import { get, getPage, post } from 'stores/_services/dataService';
import BaseListStore, { BaseListStoreArgs, DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import { IDataFetchOptions, ISearchOptions } from '../types';

export interface IFileInstance {
    id: string;
    sha1: string;
    fileRepId: string;
    name?: string;
}

interface IInterfaceListStoreArgs<T> extends BaseListStoreArgs<T> {
    itemKey?: string;
    itemId?: string;
}

export default class BaseSearchListStore<T> extends BaseListStore<T> {
    protected readonly itemKey?: string;
    protected readonly itemId?: string;

    constructor({ itemKey, itemId, ...listStoreArgs }: IInterfaceListStoreArgs<T>) {
        super(listStoreArgs);

        makeObservable(this, {
            fetchSearchResults: action,
            fetchSearchHistory: action
        });

        if (itemKey && itemId) {
            this.itemKey = itemKey;
            this.itemId = itemId;
        }
    }

    // public get instances(): unknown {
    //     throw new Error('Instances getter undefined')
    // }

    async search(args: IDataFetchOptions = {}, searchArgs: ISearchOptions = {}): Promise<void> {
        const { scanType, startDateTime: startDate, endDateTime: endDate, type: relativeTimePeriod, search: searchText } = searchArgs;

        this._updateListState({
            ...(Object.keys(args).length && {
                sortBy: args.sortBy,
                sortDirection: args.sortDirection
            }),
            filter: {
                search: searchText,
                dateTime: {
                    from: startDate,
                    to: endDate,
                    relativeTimePeriod: relativeTimePeriod
                }
            }
        });

        const options = {
            query: {
                scanType: scanType,
                startDate: startDate,
                endDate: endDate,
                searchText: searchText,
                sortBy: this.sortBy,
                sortOrder: this.sortDirection,
                relativeTimePeriod: relativeTimePeriod
            }
        };

        const { data } = await post('searchRequests', options);

        return data.id;
    }

    async searchById(Id: string): Promise<void> {
        const { data } = await get(`searchRequests/${Id}`);
        if (data.status === 'completed') {
            this.totalCount = data.totalRecords;

            this._updateListState({
                filter: {
                    search: data.query.searchText,
                    searchRequestId: Id,
                    dateTime: {
                        from: data.query.startDate,
                        to: data.query.endDate,
                        relativeTimePeriod: data.query.relativeTimePeriod
                    }
                }
            });
        }

        return data;
    }

    async fetchSearchResults(Id: string, args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        this._updateListState({
            ...(Object.keys(args).length && {
                sortBy: args.sortBy,
                sortDirection: args.sortDirection
            })
        });

        const options = {
            queryParams: {
                searchRequestId: Id,
                page: pageNumber,
                pageSize: pageSize,
                ...(Object.keys(args).length && {
                    sortOrder: args.sortDirection,
                    sortField: args.sortBy
                })
            }
        };

        const { data } = await getPage('searchResults/results', options);
        const records = data.map((elem) => ({ ...elem.data, id: elem.id }));

        if (!records.length) {
            this.clear();
        }

        this._updateData(records);
        this._updateIds(records, 0);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = records.length;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async fetchSearchHistory(args = {}): Promise<string | void> {
        const shouldReset = this._updateListState(args);

        const options = {
            filter: {
                order: this.sort
            }
        };

        const { data } = await get('searchRequests', options);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);
        this.count = data.length;
    }

    async fetchLatestSearchHistory(type: string): Promise<unknown | void> {
        const options = {
            filter: {
                order: `created_on ${SortDirection.DESC}`,
                limit: 1,
                where: {
                    'query.scanType': {
                        inq: [type]
                    }
                }
            }
        };

        const { data } = await get('searchRequests', options);

        return data && data[0];
    }

    async searchResultById(Id: string): Promise<void> {
        const {
            data: { data: result }
        } = await get(`searchResults/${Id}`);
        return result;
    }

    public async getFileDetail(fileRepId: string): Promise<void> {
        const res = await get(`fileRepLocals/${fileRepId}`);

        const { data } = res;

        if (!data.fileRepId) {
            data.fileRepId = data.sha1;
        }

        this._updateData(data);
    }

    async exportToCsv(id: string, searchType: string): Promise<void> {
        const data = {
            searchRequestId: id,
            searchType: searchType,
            type: 'export'
        };

        await post('searchResults/exportReport', data);
    }
}
